import { vars } from '@styles/vanilla-extract/theme.css';
import React, { ReactNode } from 'react';
import BoldVerticalBarChart, {
	BoldVerticalBarChartProps,
} from '@components/report/chart/bold-vertical-bar-chart/BoldVerticalBarChart';
import ColoredChartText from '@components/report/chart/colored-chart-text/ColoredChartText';
import RadialBarChart, {
	RadialBarChartProps,
} from '@components/report/chart/radial-bar-chart/RadialBarChart';
import SymmetricVerticalBarChart, {
	SymmetricVerticalBarChartProps,
} from '@components/report/chart/symmetric-vertical-bar-chart/SymmetricVerticalBarChart';
import ColoredSection from '@components/report/colored-section/ColoredSection';

import BlogPostContentItem, {
	BlogPostContentItemProps,
} from '@components/report/content-items/blog-post-content-item/BlogPostContentItem';

import ReportCard from '@components/report/report-card/ReportCard';

import SnapCarousel from '@shared/snap-carousel/SnapCarousel';

import * as styles from './posts.css';
import {
	getCategoriesByStatus,
	getHashtagsByStatus,
	getHashtagsString,
	getPostMetricsByStatus,
} from './utils';

export type PostCategories = Pick<
	SymmetricVerticalBarChartProps,
	'categories' | 'topData'
> & {
	desc?: ReactNode;
	largestCategoryValue?: RadialBarChartProps['value'];
};

export interface PostMetrics {
	averageViews?: string;
	averageImages?: string;
	relatedLocations?: string[];
}

export interface PostsProps {
	bannerHashtags?: string[];
	postsCarousel?: BlogPostContentItemProps[];
	postCategories?: PostCategories;
	postMetrics?: PostMetrics;
	adPostCategories?: PostCategories;
	adPostRate?: BoldVerticalBarChartProps;

	isLoading?: boolean;
	isError?: boolean;
	lackOfPermission?: boolean;
}

const Posts = ({
	bannerHashtags,
	postsCarousel = [],
	postCategories,
	postMetrics,
	adPostCategories,
	adPostRate,

	isLoading,
	isError,
	lackOfPermission,
	...props
}: PostsProps) => {
	const status = { isLoading, isError, lackOfPermission };
	const available = !isLoading && !isError && !lackOfPermission;

	const hashTagsArray = getHashtagsByStatus(bannerHashtags, status);
	const hashTagsString = getHashtagsString(hashTagsArray);

	return (
		<div className={styles.layout}>
			{/* 해시태그 */}
			<ColoredSection
				tooltip={
					'최근 10개의 콘텐츠를 기준으로 자주 사용된 해시태그 5개를 보여줍니다. 주요 해시태그 데이터는 인플루언서가 어떤 주제나 키워드에 집중하고 있는지 파악하는데 도움이 됩니다.'
				}
				{...status}
			>
				많이 사용된 주요 해시태그 5가지는 <span>{hashTagsString}</span> 입니다.
			</ColoredSection>

			{/* 포스트 캐러셀 */}
			<SnapCarousel
				className={lackOfPermission ? styles.lackOfPermissionCarousel : ''}
				items={postsCarousel?.map((item, idx) => (
					<BlogPostContentItem key={idx} {...item} {...status} />
				))}
			/>

			<div className={styles.gridLayout}>
				<ReportCard
					label="게시글 카테고리"
					tooltip="AI가 게시물, 댓글, 해시태그 등을 분석하여 최근 10개 게시글 카테고리를 측정한 값입니다."
					lackOfPermission={lackOfPermission}
					height={320}
				>
					<div className={styles.postsCategoryLayout}>
						<ColoredChartText {...status} subText="최근 10개 게시글 기준">
							{postCategories?.desc}
						</ColoredChartText>

						<div className={styles.postsCategoryContainer}>
							<SymmetricVerticalBarChart
								barWidth={72}
								barHeight={104}
								categoriesColor={vars.semantic.color.text[3]}
								{...getCategoriesByStatus(
									{
										topData: postCategories?.topData,
										categories: postCategories?.categories,
									},
									status,
								)}
								{...status}
							/>
							<RadialBarChart
								value={postCategories?.largestCategoryValue}
								isPositive
								{...status}
							/>
						</div>
					</div>
				</ReportCard>

				<div className={styles.verticalSplitGrid}>
					<ReportCard
						label="게시글 일 평균 조회 수"
						tooltip="최근 10개의 콘텐츠를 기준으로 계산된 게시글 평균 조회 수입니다. 조회 수란 게시글을 1회 이상 본 수입니다."
						height="auto"
						className={styles.verticalSplitGridItem}
						lackOfPermission={lackOfPermission}
					>
						<ColoredChartText>
							<span>
								{getPostMetricsByStatus(`${postMetrics?.averageViews}회`, status)}
							</span>
						</ColoredChartText>
					</ReportCard>
					<ReportCard
						label="게시글 평균 이미지 수"
						tooltip="최근 10개의 콘텐츠를 기준으로 계산된 게시글 평균 이미지 수입니다. 이미지 수란 게시글 1개당 첨부된 이미지의 수입니다."
						height="auto"
						className={styles.verticalSplitGridItem}
						lackOfPermission={lackOfPermission}
					>
						<ColoredChartText>
							<span>
								{getPostMetricsByStatus(`${postMetrics?.averageImages}개`, status)}
							</span>
						</ColoredChartText>
					</ReportCard>
					<ReportCard
						label="게시글 연관 지역"
						tooltip="최근 10개의 콘텐츠를 기준으로 계산된 게시글 연관 지역입니다. 게시글 연관 지역이란 게시글에서 언급되거나 링크된 주소입니다."
						height="auto"
						className={styles.verticalSplitGridItem}
						lackOfPermission={lackOfPermission}
					>
						<ColoredChartText>
							<span>
								{getPostMetricsByStatus(
									postMetrics?.relatedLocations?.join(', ') ?? '-',
									status,
								)}
							</span>
						</ColoredChartText>
					</ReportCard>
				</div>

				<ReportCard
					label="광고 게시글 카테고리"
					tooltip="AI가 게시물, 댓글, 해시태그 등을 분석하여 최근 10개 게시글 중 광고 게시글 카테고리를 측정한 값입니다."
					lackOfPermission={lackOfPermission}
					height={320}
				>
					<div className={styles.postsCategoryLayout}>
						<ColoredChartText {...status} subText="최근 10개 게시글 기준">
							{adPostCategories?.desc}
						</ColoredChartText>
						<div className={styles.postsCategoryContainer}>
							<RadialBarChart
								value={adPostCategories?.largestCategoryValue}
								isPositive
								{...status}
							/>
							<SymmetricVerticalBarChart
								barWidth={72}
								barHeight={104}
								categoriesColor={vars.semantic.color.text[3]}
								{...getCategoriesByStatus(
									{
										topData: adPostCategories?.topData,
										categories: adPostCategories?.categories,
									},
									status,
								)}
								{...status}
							/>
						</div>
					</div>
				</ReportCard>
				<ReportCard
					label="광고 게시글 비율"
					tooltip="광고 게시글의 비율을 유사 그룹과 비교하여 나타냅니다."
					height={320}
					lackOfPermission={lackOfPermission}
				>
					<BoldVerticalBarChart
						desc
						data={[{ isMain: true }, {}]}
						{...adPostRate}
						{...status}
					/>
				</ReportCard>
			</div>
		</div>
	);
};

export default Posts;
